import { supabase } from '../services/api/client';

// API 기본 URL 정의
export const API_URL = process.env.REACT_APP_API_URL || 'https://bll-pro-git-update-version-khanyongs-projects.vercel.app';

// 세션 유효성 검사 (Supabase가 자동으로 관리하므로 래퍼만 제공)
export const ensureValidSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('세션 확인 오류:', error);
    return null;
  }
  return session;
};

// 세션 변경 감지 설정
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
  } else if (event === 'TOKEN_REFRESHED') {
  } else if (event === 'SIGNED_IN') {
  }
});

// API 요청 함수 (세션 검증 포함)
export const fetchWithToken = async (url, options = {}) => {
  try {
    const session = await ensureValidSession();

    if (!session) {
      throw new Error('유효한 인증 세션이 없습니다.');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API 요청 실패: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('API 요청 오류:', error);
    throw error;
  }
};

// Supabase 쿼리 실행 전 세션 검증
export const executeWithValidSession = async (queryFunction) => {
  const session = await ensureValidSession();
  if (!session) {
    throw new Error('유효한 인증 세션이 없습니다.');
  }
  return await queryFunction();
};

// 로그인 상태 확인 (사용자가 승인되었는지도 확인)
export const checkAuthStatus = async () => {
  try {
    const session = await ensureValidSession();

    if (!session?.user) {
      return { isAuthenticated: false, isApproved: false, user: null };
    }

    // 사용자 프로필에서 승인 상태 확인
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_approved, email_verified, role')
      .eq('id', session.user.id)
      .single();

    if (error) {
      console.error('프로필 확인 오류:', error);
      return { isAuthenticated: true, isApproved: false, user: session.user };
    }

    return {
      isAuthenticated: true,
      isApproved: profile?.is_approved || false,
      isEmailVerified: profile?.email_verified || false,
      user: session.user,
      profile: profile
    };
  } catch (error) {
    console.error('인증 상태 확인 오류:', error);
    return { isAuthenticated: false, isApproved: false, user: null };
  }
};

// 인증 관련 함수들
export const auth = {
  // 로그인
  signIn: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('로그인 오류:', error);
      throw error;
    }

    return {
      user: data.user,
      session: data.session
    };
  },

  // 로그아웃
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('로그아웃 오류:', error);
      throw error;
    }

    // 로그아웃 후 홈페이지로 이동
    window.location.href = '/';
  },

  // 현재 사용자 조회
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('사용자 조회 오류:', error);
      throw error;
    }
    return user;
  },

  // 세션 확인
  isAuthenticated: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  }
};

export const handlePasswordReset = async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

export const getAuthenticatedUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const withAuthRefresh = async (apiCall) => {
  const user = await getAuthenticatedUser();
  if (!user) {
    throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
  }
  return await apiCall(user);
};

// 장시간 세션용 자동 저장 유틸리티 (단순화)
export const createAutoSaveSession = (saveFunction, intervalMinutes = 5) => {
  let autoSaveInterval = null;

  const startAutoSave = () => {
    if (autoSaveInterval) clearInterval(autoSaveInterval);

    autoSaveInterval = setInterval(async () => {
      try {
        const user = await getAuthenticatedUser();
        if (user) await saveFunction(user);
      } catch (error) {
        console.error('자동 저장 중 오류:', error);
      }
    }, intervalMinutes * 60 * 1000);
  };

  const stopAutoSave = () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      autoSaveInterval = null;
    }
  };

  return { startAutoSave, stopAutoSave };
};

export const showSessionExtensionNotice = (onExtend) => {
  // 더 이상 수동 연장이 필요하지 않음 (Supabase가 자동 처리)
  return true;
};

export const cleanupSession = () => {
  localStorage.removeItem('sessionExtensionShown');
};

// Supabase 쿼리를 안전하게 실행하는 유틸리티 함수
export const executeSupabaseQuery = async (queryFn, options = {}) => {
  const {
    timeout = 10000, // 기본 10초 타임아웃
    requireAuth = false, // 인증 필수 여부
    onTimeout = null, // 타임아웃 시 콜백
    onError = null // 에러 시 콜백
  } = options;

  try {
    // 1. 세션 유효성 확인 및 갱신
    console.log('executeSupabaseQuery: 세션 확인 중...');
    const session = await ensureValidSession();

    if (!session && requireAuth) {
      throw new Error('유효한 세션이 없습니다. 로그인이 필요합니다.');
    }

    if (!session && !requireAuth) {
      console.warn('executeSupabaseQuery: 세션이 없지만 계속 진행합니다.');
    }

    // 2. 타임아웃과 함께 쿼리 실행
    console.log('executeSupabaseQuery: 쿼리 실행 중...');
    const queryPromise = queryFn();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('쿼리 타임아웃')), timeout)
    );

    const result = await Promise.race([queryPromise, timeoutPromise]);

    // 3. 에러 체크
    if (result.error) {
      console.error('executeSupabaseQuery: 쿼리 에러:', result.error);

      // 인증 관련 에러 처리
      if (result.error.message?.includes('JWT') ||
        result.error.message?.includes('token') ||
        result.error.code === 'PGRST301') {
        console.log('executeSupabaseQuery: 인증 오류 감지');

        if (onError) {
          onError(result.error);
        }
      }

      throw result.error;
    }

    console.log('executeSupabaseQuery: 쿼리 성공');
    return result;

  } catch (error) {
    console.error('executeSupabaseQuery: 실행 중 오류:', error);

    // 타임아웃 처리
    if (error.message === '쿼리 타임아웃') {
      console.log('executeSupabaseQuery: 타임아웃 발생');

      if (onTimeout) {
        onTimeout();
      } else {
        // 기본 타임아웃 처리
        alert('요청 시간이 초과되었습니다. 페이지를 새로고침해주세요.');
      }
    }

    // 에러를 다시 throw하여 호출자가 처리할 수 있도록 함
    throw error;
  }
};